import { useCallback, useEffect, useRef } from "react";

import { track } from "jiffy-analytics";
import { api } from "jiffy-api";

interface UseEnhancementPollingOptions {
  artworkId: number;
  startTime: number;
  onEnhancementComplete: (artwork: any) => void;
  onEnhancementFailed?: (errorMessage: string) => void;
  enabled?: boolean;
  isArtworkPresent?: boolean;
}

interface PollingState {
  isPolling: boolean;
  pollCount: number;
  timeoutId?: NodeJS.Timeout | undefined;
}

const INITIAL_DELAY = 30000;
const POLLING_INTERVAL = 10000;
const MAX_POLLING_DURATION = 180000;

export function useEnhancementPolling({
  artworkId,
  startTime,
  onEnhancementComplete,
  onEnhancementFailed,
  enabled = true,
  isArtworkPresent = true
}: UseEnhancementPollingOptions) {
  const pollingState = useRef<PollingState>({
    isPolling: false,
    pollCount: 0
  });

  const stopPolling = useCallback(() => {
    if (pollingState.current.timeoutId) {
      clearTimeout(pollingState.current.timeoutId);
      pollingState.current.timeoutId = undefined;
    }
    pollingState.current.isPolling = false;
  }, []);

  const pollArtworkDetails = useCallback(async () => {
    if (!enabled || !pollingState.current.isPolling) {
      return;
    }

    if (pollingState.current.pollCount === 1 && artworkId) {
      track("fallback_polling_started", {
        artwork_id: String(artworkId),
        page: "design_lab"
      });
    }

    try {
      const response = await api.dtf.getArtworkHistory({
        artwork_ids: [artworkId],
        catalog_number: "DTF001"
      } as any);

      const artwork = response.artworks.find((a: any) => Number(a.id) === artworkId);

      const enhancementState =
        (artwork as any)?.current_enhancement_state || (artwork as any)?.currentEnhancementState;

      if (enhancementState === "complete_processing") {
        stopPolling();

        track("fallback_polling_enhancement_completed", {
          artwork_id: String(artworkId),
          page: "design_lab"
        });

        if (artwork) {
          const { ordered, original } = artwork as any;

          const artworkData = {
            id: artwork.id,
            noBgUrl: ordered?.no_bg_url || ordered?.noBgUrl,
            noBgNoTrimUrl: ordered?.no_bg_no_trim_url || ordered?.noBgNoTrimUrl,
            withBgUrl: ordered?.with_bg_url || ordered?.withBgUrl,
            imageUrl: ordered?.image_url || ordered?.imageUrl,
            originalImageUrl: original?.image_url || original?.imageUrl,
            canRemoveBg: ordered?.can_remove_bg ?? ordered?.canRemoveBg,
            removeBackground: ordered?.remove_background ?? ordered?.removeBackground,
            metadata: ordered?.metadata,
            enhancedMetadata: ordered?.enhanced_metadata || ordered?.enhancedMetadata,
            originalMetadata: ordered?.original_metadata || ordered?.originalMetadata,
            withBgMetadata: ordered?.with_bg_metadata || ordered?.withBgMetadata
          };

          onEnhancementComplete(artworkData);
        }
        return;
      }
    } catch (error) {
      // Continue polling on error
      window.Honeybadger?.notify("Enhancement polling error", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          error_object: error instanceof Error ? error.toString() : String(error),
          stack_trace: error instanceof Error ? error.stack : null,
          hook: "useEnhancementPolling",
          artworkId
        }
      });
    }

    // Schedule next poll after processing
    if (!pollingState.current.isPolling) {
      return;
    }

    const elapsedTime = Date.now() - startTime;

    if (elapsedTime >= MAX_POLLING_DURATION) {
      stopPolling();

      if (artworkId) {
        const artworkDetails = await api.dtf.getArtwork(
          { id: artworkId },
          { catalog_number: "DTF001" }
        );
        if (artworkDetails.enhancement_status === "failed") {
          onEnhancementFailed?.("Something went wrong. Please try again.");
        }

        track("fallback_polling_stopped", {
          artwork_id: String(artworkId),
          page: "design_lab"
        });
      }
      return;
    }

    const nextInterval = POLLING_INTERVAL;
    const remainingTime = MAX_POLLING_DURATION - elapsedTime;

    if (nextInterval > remainingTime) {
      return;
    }

    pollingState.current.timeoutId = setTimeout(() => {
      pollingState.current.pollCount += 1;
      pollArtworkDetails();
    }, nextInterval) as unknown as NodeJS.Timeout;
  }, [artworkId, startTime, onEnhancementComplete, onEnhancementFailed, enabled, stopPolling]);

  const startPolling = useCallback(() => {
    if (pollingState.current.isPolling || !enabled) {
      return;
    }

    pollingState.current.isPolling = true;
    pollingState.current.pollCount = 0;

    pollingState.current.timeoutId = setTimeout(() => {
      pollingState.current.pollCount += 1;
      pollArtworkDetails();
    }, INITIAL_DELAY) as unknown as NodeJS.Timeout;
  }, [enabled, pollArtworkDetails]);

  useEffect(() => {
    if (!isArtworkPresent && pollingState.current.isPolling) {
      stopPolling();

      if (artworkId) {
        track("fallback_polling_artwork_removed", {
          artwork_id: String(artworkId),
          page: "design_lab"
        });
      }
    }
  }, [isArtworkPresent, stopPolling, artworkId]);

  useEffect(
    () => () => {
      stopPolling();
    },
    [stopPolling]
  );

  return {
    startPolling,
    stopPolling,
    isPolling: pollingState.current.isPolling
  };
}
